<!--
  Route-fork PR-R2b + design-parity PR-1: DistributorLayout.

  Wraps the Distributor surface (`/distributor/*` work + bare `/`). Chrome:

    - Shared Topbar (frontend/src/layouts/Topbar.vue) -- eyebrow + title
      + back-arrow + autosave indicator + Log out affordance.
    - 3-group sidebar: Work / Reporting / Account.
    - ImpersonationBanner slot -- shown when the impersonation store reports
      an active session.

  Topbar title: "<Distributor name> > <Page name>" under the
  "Distributor Account" eyebrow.
    - Distributor name: the effective tenant (impersonated distributor
      while a SuperAdmin impersonates). Omitted until known.
    - Page name: on the store editor (/distributor/stores/:id/edit[/:section])
      the editor's last-crumb label (useTopBar()), AND the back-arrow points
      at /distributor/stores. Elsewhere the route's title from
      router/distributorTopbarTitles.js (shared with the sidebar labels).

  Capability-driven sidebar visibility (Distributor App sub-task 2,
  migration route_fork_schema_012):
    - Work group + shared Reporting links are visible for all logged-in
      distributor users (Owner + Builder).
    - Account group + Payouts: gated on `distributor.account.read`
      (Owner + SA). Builder gets a slimmed informational stub instead.

  Capabilities source: `distributorLayoutCapabilities` injection token,
  populated by App.vue from Pinia's useAccountStore. Injection (rather
  than direct Pinia import) keeps mount tests cheap.
-->
<template>
    <div class="dist-layout">
        <Topbar
            :back-href="backHref"
            eyebrow="Distributor Account"
            :title="topbarText"
            :save-state="showSaveIndicator ? saveState : null"
            data-testid="dist-topbar"
        >
            <!--
              "<Distributor name> > <Page name>" (Jake, 2026-10-19). Separate
              spans with their own testids so scenarios can assert each part.
              While the name is unknown only the page name renders: no
              dangling separator, never "undefined".
            -->
            <template #title>
                <span
                    v-if="distributorName"
                    class="dist-topbar__name"
                    :title="distributorName"
                    data-testid="dist-topbar-distributor-name"
                    >{{ distributorName }}</span
                >
                <span
                    v-if="distributorName"
                    class="dist-topbar__sep"
                    aria-hidden="true"
                    > &gt; </span
                >
                <span data-testid="dist-topbar-page-name">{{ pageTitle }}</span>
            </template>
            <template #primaryAction>
                <slot name="topbar-primary-action" />
            </template>
            <!--
              Personal Profile entry point (card
              https://trello.com/c/BxskXBad): an icon in the top-right
              corner NEXT TO Log out, in all four apps. Same placement and
              same affordance everywhere so the personal page feels native
              in each chrome while being the one shared surface.
            -->
            <!--
              HELP -- the primary, always-visible entry point into the
              help flyover.

              Jake, after using the shipped help: "a help button that's not
              discoverable enough... put it in the top bar next to the
              profile icon, and make it green so it's easy to see." It used
              to be a sidebar row in the REPORTING group, which is below the
              fold on a short viewport and reads as one more nav
              destination rather than a standing affordance.

              PLACED IMMEDIATELY BEFORE the profile icon, so the two
              always-available personal controls sit together at the end of
              the bar and Help is the first of them (it is the one being
              promoted). `help-topbar-affordance.spec.js` pins the
              adjacency by DOM order, not by CSS.

              STILL A BUTTON THAT OPENS THE FLYOVER, never a link: a reader
              mid-edit must not lose the screen they are asking about. The
              target stays contextual through `resolveHelpTarget` -- the
              flyover computes it from the live route, so Help from the
              store editor still opens "Build a store". Nothing here
              resolves or duplicates that.

              Icon AND label. The bar is compact, but "Help" is one short
              word and a labelled green pill is materially more
              discoverable than a bare glyph -- discoverability being the
              entire point of the change. The aria-label is kept anyway so
              the accessible name does not depend on the icon ligature
              text leaking into it.
            -->
            <button
                type="button"
                class="dist-topbar__help"
                data-testid="dist-topbar-help"
                aria-label="Help"
                title="Help"
                :aria-expanded="helpFlyoverOpen ? 'true' : 'false'"
                aria-haspopup="dialog"
                @click="openHelpFlyover"
            >
                <v-icon icon="help_outline" size="18" aria-hidden="true" />
                <span>Help</span>
            </button>
            <router-link
                to="/distributor/profile"
                class="dist-topbar__profile"
                data-testid="dist-topbar-profile"
                title="Profile"
                aria-label="Profile"
            >
                <v-icon icon="account_circle" size="22" />
            </router-link>
            <button
                type="button"
                class="dist-topbar__logout"
                data-testid="dist-topbar-logout"
                @click="logout"
            >
                Log out
            </button>
        </Topbar>

        <div class="dist-banner-slot">
            <impersonation-banner />
        </div>

        <div class="dist-body">
            <aside class="dist-sidebar">
                <!--
                  DASHBOARD IS THE FIRST ROW, ABOVE Work (Jake, 2026-09-24).
                  It is the landing surface `postLoginRedirect` sends
                  dashboard-capable users to, so the nav should agree with
                  where login already lands rather than leaving `/distributor`
                  reachable only by the topbar back arrow.

                  Headingless by design: a one-row group with an eyebrow above
                  it reads as a section with a missing sibling. The three
                  labelled groups below stay as they are.

                  Gated on the SAME capability as the route
                  (`distributor.reporting.read`). A Sales Exec lacks it and
                  would be bounced to /distributor/stores on arrival, so
                  offering the row would be an affordance that bounces.
                -->
                <section
                    v-if="hasDashboardAccess"
                    data-testid="dist-sidebar-dashboard"
                >
                    <ul>
                        <li>
                            <sidebar-nav-item
                                to="/distributor"
                                icon="dashboard"
                                :label="NAV_LABELS.dashboard"
                                key-name="dashboard"
                                static-sub-text="Setup and performance"
                                :summary-slot="summary?.dashboard || null"
                            />
                        </li>
                    </ul>
                </section>
                <section data-testid="dist-sidebar-work">
                    <h3>Work</h3>
                    <ul>
                        <li>
                            <sidebar-nav-item
                                to="/distributor/stores"
                                icon="storefront"
                                :label="NAV_LABELS.stores"
                                key-name="stores"
                                :summary-slot="summary?.stores || null"
                            />
                        </li>
                        <li>
                            <sidebar-nav-item
                                to="/distributor/decoration-library"
                                icon="auto_awesome"
                                :label="NAV_LABELS.decorationLibrary"
                                key-name="decoration_library"
                                :summary-slot="summary?.decoration_library || null"
                            />
                        </li>
                        <li>
                            <!--
                              Approval Workspace (Phase 2.1, card
                              https://trello.com/c/hYlwyKYT). Sits after
                              Decoration Library per P2-Q12 (workflow:
                              Library -> Approvals). Sub-text is "N pending .
                              M needs attention", surfaced from the
                              ``approvals`` summary slot the dashboard
                              endpoint produces. Owner + Builder both hold
                              the read capability that gates this surface.
                            -->
                            <sidebar-nav-item
                                to="/distributor/decorations/approvals"
                                icon="fact_check"
                                :label="NAV_LABELS.approvals"
                                key-name="approvals"
                                :summary-slot="summary?.approvals || null"
                            />
                        </li>
                        <li v-if="hasClientsAccess">
                            <!--
                              Clients tab (issue #8). Gated on
                              ``client.read`` -- both Owner and Builder
                              hold the cap, so this item is visible to
                              both. The Add / Edit / Delete affordances
                              inside the surface check ``client.update``
                              (Owner only).

                              Icon: Material ligature "group" (per
                              project_vuetify-icon-set-is-md-not-mdi.md
                              -- the default set is 'md', not 'mdi').

                              Sub-text ("N clients" / "N clients · M
                              onboarding") + status dot come from the
                              ``clients`` summary slot the dashboard
                              endpoint produces.
                            -->
                            <sidebar-nav-item
                                to="/distributor/clients"
                                icon="group"
                                :label="NAV_LABELS.clients"
                                key-name="clients"
                                :summary-slot="summary?.clients || null"
                            />
                        </li>
                    </ul>
                </section>

                <section data-testid="dist-sidebar-reporting">
                    <h3>Reporting</h3>
                    <ul>
                        <li>
                            <sidebar-nav-item
                                to="/distributor/reporting/orders"
                                icon="receipt_long"
                                :label="NAV_LABELS.orders"
                                key-name="orders"
                                :summary-slot="summary?.orders || null"
                            />
                        </li>
                        <li>
                            <!--
                              Performance: static descriptor, no dot. Plan
                              section 14: no negative state to flag here.
                            -->
                            <sidebar-nav-item
                                to="/distributor/reporting/performance"
                                icon="bar_chart"
                                :label="NAV_LABELS.performance"
                                key-name="performance"
                                :summary-slot="null"
                                static-sub-text="GMV, top products, trends"
                            />
                        </li>
                        <li>
                            <!--
                              Reports: static descriptor, no dot. Nothing
                              here can be in a negative state -- the page is
                              a generator, not a queue.
                            -->
                            <sidebar-nav-item
                                to="/distributor/reporting/reports"
                                icon="description"
                                :label="NAV_LABELS.reports"
                                key-name="reports"
                                :summary-slot="null"
                                static-sub-text="Store, batch and date filters"
                            />
                        </li>
                        <li v-if="hasFullAccountGroup">
                            <sidebar-nav-item
                                to="/distributor/reporting/payouts"
                                icon="request_quote"
                                :label="NAV_LABELS.payouts"
                                key-name="payouts"
                                :summary-slot="summary?.payouts || null"
                            />
                        </li>
                        <!--
                          Help USED TO LIVE HERE, as the last row of the
                          Reporting group. It moved to the topbar (see the
                          `dist-topbar-help` control above) because a
                          sidebar row at the bottom of the second group is
                          exactly what "not discoverable enough" described.

                          It was NOT duplicated. Two entry points to one
                          panel is how the first one stayed easy to miss,
                          and it would mean two places to keep contextually
                          correct. `help-topbar-affordance.spec.js` asserts
                          this row stays gone.
                        -->
                    </ul>
                </section>

                <section
                    v-if="hasFullAccountGroup"
                    data-testid="dist-sidebar-account"
                >
                    <h3>Account</h3>
                    <ul>
                        <li>
                            <!--
                              Company (was Company Profile) -- the distributor's business
                              info. Renamed from "Profile" and repointed
                              from /account/profile with the 2026-08-11
                              consolidation (card
                              https://trello.com/c/BxskXBad), which freed
                              plain "Profile" for the PERSONAL page reached
                              from the topbar profile icon.

                              Integrations used to be this page's second
                              tab; it is the sibling row below as of
                              2026-09-24.
                            -->
                            <sidebar-nav-item
                                to="/distributor/account/company"
                                icon="business"
                                :label="NAV_LABELS.company"
                                key-name="company"
                                :summary-slot="summary?.company || null"
                            />
                        </li>
                        <li>
                            <!--
                              Suppliers (was Integrations; Stripe Connect now lives only on Payments) -- supplier
                              accounts. Jake, 2026-09-24: move it out of
                              Company Profile "to the Account section in the
                              left-nav as their own page, just below the
                              Company Profile page".

                              POSITION IS THE REQUIREMENT, so it is asserted:
                              distributor-integrations-page.spec.js pins this
                              row as the one IMMEDIATELY after Company
                              Profile, not merely present in the group.

                              Dot from `summary.suppliers`: amber while the
                              "at least one supplier connected" launch gate
                              is open (derived server-side from the setup
                              checklist, IA unit U7). key-name stays
                              "integrations" so existing testids hold.
                            -->
                            <sidebar-nav-item
                                to="/distributor/account/suppliers"
                                icon="power"
                                :label="NAV_LABELS.suppliers"
                                key-name="integrations"
                                :summary-slot="summary?.suppliers || null"
                            />
                        </li>
                        <li>
                            <sidebar-nav-item
                                to="/distributor/account/team"
                                icon="group"
                                :label="NAV_LABELS.team"
                                key-name="team"
                                :summary-slot="summary?.team || null"
                            />
                        </li>
                        <li>
                            <sidebar-nav-item
                                to="/distributor/account/payments"
                                icon="credit_card"
                                :label="NAV_LABELS.payments"
                                key-name="payments"
                                :summary-slot="summary?.payments || null"
                            />
                        </li>
                        <li>
                            <!--
                              Defaults binds to summary.pricing (composite
                              row -- pricing is the primary slot per the
                              plan's locked mapping).
                            -->
                            <sidebar-nav-item
                                to="/distributor/account/defaults"
                                icon="settings"
                                :label="NAV_LABELS.storeDefaults"
                                key-name="pricing"
                                :summary-slot="summary?.pricing || null"
                            />
                        </li>
                        <li>
                            <!--
                              Compliance binds to summary.tax_nexus
                              (composite row -- tax_nexus is the primary
                              slot per the plan's locked mapping).
                            -->
                            <sidebar-nav-item
                                to="/distributor/account/compliance"
                                icon="gavel"
                                :label="NAV_LABELS.compliance"
                                key-name="tax_nexus"
                                :summary-slot="summary?.tax_nexus || null"
                            />
                        </li>
                        <!--
                          HIDDEN 2026-09-03 -- account-level Notifications.
                          The page rendered the 19-event roster
                          (api/src/notifications/event_roster.py), which has
                          ZERO overlap with the live senders: toggling those
                          events changed nothing about what mail anyone
                          received. Superseded by the STORE-level matrix in
                          the store editor's Email Notifications card, whose
                          toggles DO gate real sends.

                          The route is unregistered in router/index.js, so
                          this link would be dead navigation. Restore both
                          together if the roster ever gains live senders.

                          <li>
                            <sidebar-nav-item
                                to="/distributor/account/notifications"
                                icon="notifications"
                                :label="NAV_LABELS.notifications"
                                key-name="notifications"
                                :summary-slot="null"
                            />
                          </li>
                        -->
                    </ul>
                </section>

                <!--
                  Builder stub. Builder holds none of the distributor.* Account
                  caps yet, so there is no Owner-gated route a Builder may
                  enter. Rendering router-links to /account/* here would be dead
                  navigation -- the requireCapability guard would bounce the
                  Builder straight back to /distributor/stores. Until a
                  Builder-scoped self-service route + cap exist (later sub-task),
                  the stub is informational text, not links.
                -->
                <div
                    v-else
                    class="dist-sidebar__account-stub"
                    data-testid="dist-sidebar-my-account"
                >
                    <span class="dist-sidebar__account-stub-label"
                        >Account &amp; Team</span
                    >
                    <span class="dist-sidebar__account-stub-note">
                        Self-service account settings are available to account
                        Owners. Ask your Owner for access.
                    </span>
                </div>
            </aside>

            <main class="dist-main">
                <!--
                  Billing incident (IA proposal section 3.2): a failed
                  ThreadFlow payment or a suspension is an incident, not a
                  setup step, so it shows on every page rather than as a row
                  on the dashboard checklist. Driven by the same summary slot
                  as the Payments dot ("red" = payment_failed / suspended).
                  F3 (Jake, 2026-10-30: "Gate+banner and a new checklist
                  row"): with no live terms the stores cannot take orders,
                  and the banner says so in the server's sentence
                  (payments.orders_blocked, the same words as the Go live
                  row) -- including an account with NO subscription, which
                  had no banner before. A canceled account gets the
                  read-only banner below instead.
                -->
                <div
                    v-if="billingIncident || (ordersBlocked && !readOnlyUntil)"
                    class="dist-billing-incident"
                    role="alert"
                    data-testid="dist-billing-incident-banner"
                >
                    <span
                        v-if="ordersBlocked"
                        data-testid="dist-billing-incident-orders-blocked"
                    >{{ ordersBlocked }}</span>
                    <template v-else>Your ThreadFlow billing needs attention.</template>
                    <a
                        href="/distributor/account/payments"
                        @click.prevent="$router && $router.push('/distributor/account/payments')"
                    >Open Payments</a>
                </div>
                <!--
                  Canceled account = read-only until the window closes (Jake,
                  2026-10-22). The API refuses every write; this says so on
                  every page, with the date, before the user tries one. The
                  date comes from the payments summary, computed by the same
                  code as the refusal itself.
                -->
                <div
                    v-if="readOnlyUntil"
                    class="dist-billing-incident"
                    role="status"
                    data-testid="dist-read-only-banner"
                >
                    Your ThreadFlow subscription is canceled, so this account is
                    read-only until {{ readOnlyUntil }}. You can view stores,
                    orders and reports, but not make changes, and your stores
                    cannot take orders. The account Owner can renew from the
                    <a
                        href="/distributor/stores"
                        @click.prevent="$router && $router.push('/distributor/stores')"
                    >Stores list</a>.
                </div>
                <slot />
            </main>
        </div>

        <!--
          Help flyover. Mounted at the LAYOUT level so it floats over every
          distributor screen (including the store editor) and survives
          navigation; it re-resolves its contextual article on each route
          change rather than being remounted per page.
        -->
        <help-flyover
            audience="owner"
            :open="helpFlyoverOpen"
            @close="closeHelpFlyover"
        />
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { inject, onMounted, onUnmounted } from "vue";
import ImpersonationBanner from "@/components/ImpersonationBanner.vue";
import SidebarNavItem from "@/layouts/SidebarNavItem.vue";
import Topbar from "@/layouts/Topbar.vue";
import HelpFlyover from "@/components/Help/HelpFlyover.vue";
import { useTopBar, _isStoreEditPath } from "@/composables/useTopBar";
import { useDistributorSummaryStore } from "@/stores/distributorSummary";
import { resolveHelpTarget } from "@/router/helpContextMap";
import {
    NAV_LABELS,
    resolveDistributorPageTitle,
} from "@/router/distributorTopbarTitles";
import { effectiveTenantId } from "@/stores/effectiveTenant";
import { useImpersonationStore } from "@/stores/impersonation";

// The dashboard-summary endpoint is gated on distributor.reporting.read.
// Normalize the injected capabilities (may be a raw array or a ref-like
// { value: [...] }) to a plain array so both setup() and the options
// `capabilities` computed agree on the read.
function _normalizeCaps(inj) {
    if (Array.isArray(inj)) return inj;
    if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
    }
    return [];
}

export default {
    name: "DistributorLayout",
    components: { HelpFlyover, ImpersonationBanner, SidebarNavItem, Topbar },
    data() {
        return {
            // Help flyover open state. Owned by the layout because the
            // flyover floats over every distributor screen and must
            // outlive navigation between them.
            helpFlyoverOpen: false,
            // Fetched name of the effective tenant; "" until known (or when
            // the fetch is refused). See loadDistributorName().
            fetchedDistributorName: "",
            NAV_LABELS,
        };
    },
    inject: {
        distributorLayoutCapabilities: {
            default: () => [],
        },
    },
    setup() {
        // useTopBar reads $route + Vuex store via composition API.
        const { crumbs, showSaveIndicator, saveState } = useTopBar();

        // Sidebar summary (sub-text + status dots + Payouts badge). The
        // store is populated by POST /api/distributor/dashboard-summary;
        // we trigger the fetch on mount. The store handles impersonation
        // stop / start via its own impersonation hook, so we do not need
        // to subscribe to impersonation changes here.
        const summaryStore = useDistributorSummaryStore();
        const impersonation = useImpersonationStore();

        // The sidebar summary comes from POST /distributor/dashboard-summary,
        // which the backend gates on distributor.reporting.read. Team members
        // (e.g. sales_executive) hold distributor.access + store-lifecycle caps
        // but NOT reporting.read -- firing the fetch for them is a guaranteed
        // 403. Guard the call so a team member gets a fully-working nav with no
        // doomed request (defense in depth: the store also swallows the 403,
        // and the template guards every summary read with `summary?.`).
        const injectedCaps = inject("distributorLayoutCapabilities", () => []);
        const canReadSummary = () =>
            _normalizeCaps(
                typeof injectedCaps === "function" ? injectedCaps() : injectedCaps
            ).includes("distributor.reporting.read");

        // Sidebar event bus (PR-1): subscribe the summary store to the
        // domain events that affect its counters so a mutation on a child
        // page refreshes the sidebar without a path change. The layout's
        // already-computed reporting.read predicate is threaded in so the
        // store enforces the cap gate per event (a team member's mutation
        // event fires no doomed 403). Additive alongside the $route.path
        // watcher below (design 7a: collapse is PR-5; a redundant refetch is
        // not a correctness bug). Torn down on unmount so no listener
        // survives a shell switch (design Section 8).
        let unsubscribeSummary = null;

        onMounted(() => {
            if (canReadSummary()) {
                summaryStore.fetch();
            }
            unsubscribeSummary = summaryStore.subscribe({ canReadSummary });
        });

        onUnmounted(() => {
            if (unsubscribeSummary) unsubscribeSummary();
        });

        return {
            crumbs,
            showSaveIndicator,
            saveState,
            summaryStore,
            canReadSummary,
            impersonation,
        };
    },
    computed: {
        billingIncident() {
            return this.summaryStore.summary?.payments?.status === "red";
        },
        // The server's sentence when the stores cannot take orders (no live
        // ThreadFlow terms), else null.
        ordersBlocked() {
            return this.summaryStore.summary?.payments?.orders_blocked || null;
        },
        // "January 20, 2027" for a canceled account, else null. The instant
        // is UTC; the calendar date is shown in UTC too, so it matches the
        // date in the API's refusal message.
        readOnlyUntil() {
            const iso = this.summaryStore.summary?.payments?.read_only_until;
            if (!iso) return null;
            const when = new Date(iso);
            if (Number.isNaN(when.getTime())) return null;
            return when.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
                timeZone: "UTC",
            });
        },
        summary() {
            return this.summaryStore.summary;
        },
        // Contextual Help target: the article about the screen the reader is
        // on, or the owner help index when this route has no article. See
        // router/helpContextMap.js -- the map is the only place to edit, and
        // its spec proves every slug in it is a real file in docs/user/.
        helpTo() {
            return resolveHelpTarget(this.$route, "owner");
        },
        capabilities() {
            const inj = this.distributorLayoutCapabilities;
            if (Array.isArray(inj)) return inj;
            if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
                return inj.value;
            }
            return [];
        },
        hasDashboardAccess() {
            // Mirrors the route guard on `/distributor` exactly. If these two
            // ever disagree the nav offers a row that redirects away.
            return this.capabilities.includes("distributor.reporting.read");
        },
        hasFullAccountGroup() {
            return this.capabilities.includes("distributor.account.read");
        },
        hasClientsAccess() {
            // Owner + Builder both hold client.read; only Owner holds
            // client.update. The sidebar entry is therefore read-gated;
            // the in-page write affordances handle the finer cap.
            return this.capabilities.includes("client.read");
        },
        // Editor mode: lit when the path matches the new store-editor
        // URL (/distributor/stores/:id/edit[/:section]). The back-arrow
        // pops back to the store list; otherwise the Distributor home is
        // the next step up.
        isStoreEditorRoute() {
            return _isStoreEditPath(this.$route?.path || "");
        },
        backHref() {
            return this.isStoreEditorRoute
                ? "/distributor/stores"
                : "/distributor";
        },
        // Page name. On the store editor the useTopBar() crumb wins (the
        // edited store's name, or "Editing store"). Everywhere else it comes
        // from the route (router/distributorTopbarTitles.js), NOT from the
        // crumbs: buildCrumbs() always leads with a "Stores" crumb for any
        // /distributor path, and getEditedCampaignName can be stale, so
        // trusting crumbs off the editor labelled every page "Stores".
        pageTitle() {
            if (this.isStoreEditorRoute) {
                const list = this.crumbs;
                if (Array.isArray(list) && list.length > 0) {
                    const last = list[list.length - 1];
                    if (last && last.label) return last.label;
                }
            }
            return resolveDistributorPageTitle(this.$route);
        },
        // Which distributor this session is acting as. While a SuperAdmin
        // impersonates, that is the IMPERSONATED distributor (the name is
        // already in the impersonation store for the banner); otherwise the
        // fetched name of the account's own tenant.
        distributorName() {
            const imp = this.impersonation;
            if (imp && imp.active && imp.distributorName) {
                return imp.distributorName;
            }
            return this.fetchedDistributorName || "";
        },
        // Plain-text form (title prop / fallback / accessible name).
        topbarText() {
            return this.distributorName
                ? `${this.distributorName} > ${this.pageTitle}`
                : this.pageTitle;
        },
        tenantId() {
            return effectiveTenantId();
        },
    },
    mounted() {
        this.loadDistributorName();
        this.$watch(() => this.tenantId, () => this.loadDistributorName());
        // The sidebar summary is fetched once in setup()'s onMounted and the
        // layout then persists across intra-surface navigations -- so a
        // mutation on a child page (e.g. creating a store) leaves the sidebar
        // sub-text stale until a full page reload (#1830). Re-fetch on every
        // in-surface PATH change so navigating away from the mutating page
        // (the natural next step after a save) pulls fresh figures. Watching
        // the PATH (not the whole route) avoids a refetch on pure query-param
        // changes (e.g. table filters), which never alter the summary.
        this.$watch(
            () => this.$route && this.$route.path,
            (next, prev) => {
                // Same reporting.read guard as the mount fetch -- a team member
                // navigating within the surface must not fire a doomed 403.
                if (next && next !== prev && this.canReadSummary()) {
                    this.summaryStore.fetch();
                }
            }
        );
    },
    methods: {
        // Reads the tenant's name from its distributor record -- the same
        // read the Company page makes (no endpoint carries the name
        // earlier: /auth/me returns only id + feature flags). Skipped while
        // impersonating with a known name. A refusal or failure degrades to
        // the page name alone; it must never raise an error toast for a
        // label.
        async loadDistributorName() {
            const id = this.tenantId;
            this.fetchedDistributorName = "";
            if (id == null) return;
            const imp = this.impersonation;
            if (imp && imp.active && imp.distributorName) return;
            try {
                const record = await this.$store.dispatch("jv/get", [
                    `/distributors/${id}`,
                    {},
                ]);
                // Drop a response for a tenant we are no longer showing.
                if (this.tenantId !== id) return;
                const name = record && typeof record.name === "string"
                    ? record.name.trim()
                    : "";
                this.fetchedDistributorName = name;
            } catch (e) {
                this.fetchedDistributorName = "";
            }
        },
        openHelpFlyover() {
            this.helpFlyoverOpen = true;
        },
        closeHelpFlyover() {
            this.helpFlyoverOpen = false;
        },
        async logout() {
            try {
                await this.$store.dispatch("logout");
                this.$router.push("/login");
            } catch (e) {
                console.error(describeError(e));
            }
        },
    },
};
</script>

<style scoped>
.dist-layout {
    /*
     * Fixed-viewport shell. height: 100% fills the parent <v-main>
     * exactly. overflow: hidden keeps the document from scrolling.
     */
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--tf-surface-light);
    overflow: hidden;
}
/*
 * Topbar logout button. Lives in the Topbar's default ("extra") slot.
 * Tokens-only.
 */
/*
 * Green Help pill, immediately left of the Profile icon.
 *
 * WHICH GREEN, and why not the obvious one. `--tf-success` (#00d084) is
 * the token literally commented "green" in the palette and is the wrong
 * choice here: at 2.03:1 against the white topbar it fails the WCAG AA
 * text minimum (4.5:1) AND the non-text/graphical minimum (3:1), so a
 * filled pill with a white label would be close to unreadable. Making the
 * control impossible to read is a strange way to make it easier to see.
 *
 * `--tf-success-dark` (#2e7d32) carries the white label at 5.13:1 --
 * clears AA text -- and reads as a shape against the bar at the same
 * 5.13:1, clearing the 3:1 non-text bar. It already has filled-background
 * precedent in the store editor's LeftRail. Pinned by
 * styles/__tests__/help-affordance-contrast.spec.js.
 *
 * On borrowing a SEMANTIC token for a nav affordance: acceptable here
 * because the topbar carries no success/failure readout for this green to
 * be confused with -- the nearest status semantics live in the editor's
 * autosave dot, which is a different surface and already uses
 * --tf-success. If a status indicator ever lands in this bar, this
 * affordance should get its own brand-green token rather than keep
 * sharing one.
 */
.dist-topbar__name {
    display: inline-block;
    max-width: 22ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}
.dist-topbar__sep {
    color: var(--tf-fg-4);
    white-space: pre;
}
.dist-topbar__help {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--tf-success-dark, #2e7d32);
    border: 1px solid var(--tf-success-dark, #2e7d32);
    color: var(--tf-fg-on-primary, #ffffff);
    border-radius: var(--tf-radius-xs);
    padding: 4px 12px;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.4;
    cursor: pointer;
    margin-right: 8px;
}

.dist-topbar__help:hover {
    /* Darken rather than lighten on hover: lightening would walk the fill
     * back toward the sub-AA end of the green scale. --tf-success-darker
     * is 7.16:1 with the white label. */
    background: var(--tf-success-darker, #256428);
    border-color: var(--tf-success-darker, #256428);
}

/*
 * Visible keyboard focus. The default UA ring is easy to lose against a
 * saturated fill, so the ring is drawn OUTSIDE the button with an offset
 * against the white bar.
 */
.dist-topbar__help:focus-visible {
    outline: 2px solid var(--tf-secondary, #133752);
    outline-offset: 2px;
}

/* Personal Profile icon, immediately left of Log out. */
.dist-topbar__profile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--tf-fg-1);
    text-decoration: none;
    margin-right: 8px;
}

.dist-topbar__profile:hover {
    opacity: 0.75;
}

.dist-topbar__logout {
    background: transparent;
    border: 1px solid var(--tf-border-strong);
    color: var(--tf-fg-1);
    border-radius: var(--tf-radius-xs);
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
}
.dist-topbar__logout:hover {
    background: var(--tf-surface-alt);
}
.dist-banner-slot {
    flex-shrink: 0;
}
.dist-body {
    display: flex;
    flex: 1;
    min-height: 0;
}
.dist-sidebar {
    width: 220px;
    flex-shrink: 0;
    background: var(--tf-bg);
    border-right: 1px solid var(--tf-border);
    padding: 16px 0;
    overflow-y: auto;
}
.dist-sidebar h3 {
    font-size: 0.8125rem;
    text-transform: uppercase;
    color: #374151;
    padding: 8px 16px;
    margin: 0;
}
.dist-sidebar ul {
    list-style: none;
    padding: 0;
    margin: 0 0 16px 0;
}
/*
 * `.dist-sidebar__item--button` and its row/icon/label rules were removed
 * with the sidebar Help row they existed for -- Help was the only
 * button-shaped entry in a sidebar of router-links. Every remaining row
 * is a <sidebar-nav-item>, which carries its own scoped styles.
 */
.dist-sidebar__account-stub { padding: 8px 16px; }
.dist-sidebar__account-stub-label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    color: var(--tf-fg-4);
    margin-bottom: 4px;
}
.dist-sidebar__account-stub-note {
    display: block;
    font-size: 12px;
    color: var(--tf-fg-quiet);
    line-height: 1.4;
}
.dist-main {
    flex: 1;
    padding: 24px;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
}
.dist-billing-incident {
    margin: 0 0 16px;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid var(--tf-error);
    background: var(--tf-error-soft);
    color: var(--tf-fg-1);
    font-weight: 500;
}
.dist-billing-incident a {
    margin-left: 8px;
    color: var(--tf-primary);
}
</style>
